/**
 * 語音功能默認配置
 * 這個文件集中管理所有語音相關的默認值和配置
 */

// 默認語音配置（可通過環境變數覆蓋）
export const VOICE_DEFAULTS = {
  service: (process.env.NEXT_PUBLIC_DEFAULT_VOICE_SERVICE as 'voicevox' | 'browser') || 'voicevox',
  gender: (process.env.NEXT_PUBLIC_DEFAULT_VOICE_GENDER as 'male' | 'female') || 'female',
  voice_id: parseInt(process.env.NEXT_PUBLIC_DEFAULT_VOICE_ID || '2'),
  voiceStyle: process.env.NEXT_PUBLIC_DEFAULT_VOICE_STYLE || 'ノーマル',
  isVIP: false
} as const

// 支援的語音風格（從 VOICEVOX 官方文檔）
export const SUPPORTED_VOICE_STYLES = [
  // 基本風格
  'ノーマル',
  
  // 情感風格
  'あまあま', 'ツンツン', 'セクシー', 'ささやき',
  
  // 情緒風格
  '喜び', '悲しみ', '怒り', '恐怖', '驚き',
  
  // 特殊風格
  'しっとり', '不機嫌', 'かなしみ', '囁き', '熱血', 'ツンギレ'
] as const

// 語音服務配置
export const VOICE_SERVICES = {
  voicevox: {
    name: 'VOICEVOX',
    description: '高品質日語語音合成',
    defaultvoice_id: 2,
    supportedStyles: SUPPORTED_VOICE_STYLES
  },
  browser: {
    name: '瀏覽器語音',
    description: '使用瀏覽器內建語音引擎',
    defaultvoice_id: 0,
    supportedStyles: ['ノーマル'] // 瀏覽器語音通常只支援基本風格
  }
} as const

// 性別對應的默認語音ID
export const GENDER_DEFAULT_VOICE_IDS = {
  female: 2, // 四国めたん
  male: 13   // 青山龍星
} as const

// 語音品質等級
export const VOICE_QUALITY_TIERS = {
  free: {
    maxVoices: 3,
    supportedStyles: ['ノーマル', '喜び', '悲しみ']
  },
  vip: {
    maxVoices: -1, // 無限制
    supportedStyles: SUPPORTED_VOICE_STYLES
  }
} as const

/**
 * 根據用戶等級獲取可用的語音風格
 */
export function getAvailableStylesForTier(isVIP: boolean): readonly string[] {
  return isVIP 
    ? VOICE_QUALITY_TIERS.vip.supportedStyles
    : VOICE_QUALITY_TIERS.free.supportedStyles
}

/**
 * 驗證語音風格是否有效
 */
export function isValidVoiceStyle(style: string, isVIP: boolean = false): boolean {
  const availableStyles = getAvailableStylesForTier(isVIP)
  return availableStyles.includes(style as any)
}

/**
 * 獲取安全的語音風格（如果無效則返回默認值）
 */
export function getSafeVoiceStyle(style: string, isVIP: boolean = false): string {
  if (isValidVoiceStyle(style, isVIP)) {
    return style
  }
  return VOICE_DEFAULTS.voiceStyle
}

/**
 * 根據性別獲取默認語音ID
 */
export function getDefaultvoice_idForGender(gender: 'male' | 'female'): number {
  return GENDER_DEFAULT_VOICE_IDS[gender]
}
